"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Profile } from "@zapier/copilot-ui";
import {
  useIsCopilotEnabled,
  useCopilotTreatments,
} from "@zapier/copilot-ui/client";
import { useCurrentUserContext } from "./CurrentUserContext";
import { createUserProfile } from "../app/app/get-help/utils/createUserProfile";
import { useAccountEntitlements } from "../app/app/get-help/hooks/useAccountEntitlements";

type CopilotContextType = {
  /** Profile formatted for ai-toolkit hooks */
  profile: Profile;

  /** Whether the copilot chat feature is available for this user (logged-in users only) */
  isCopilotEnabled: boolean;
  /** Copilot has been explicitly turned off (enterprise opt-out OR no-pilot experiment) */
  isCopilotDisabled: boolean;
  /** Enterprise opt-out from entitlements */
  isCopilotOptOut: boolean;
  /** Disabled by no-pilot experiment treatment */
  isCopilotNoPilotDisabled: boolean;

  /** All checks complete and ready to use */
  isReady: boolean;
  /** Error from fetching user data */
  error: unknown;
};

const CopilotContext = createContext<CopilotContextType | null>(null);

type CopilotContextProviderProps = {
  children: ReactNode;
};

/**
 * Parse an env value as a boolean override.
 * Returns true/false if set, undefined if not set.
 */
function parseEnvBoolean(value: string | undefined): boolean | undefined {
  if (value === "on" || value === "true") return true;
  if (value === "off" || value === "false") return false;
  return undefined;
}

// Development-only mock overrides for testing different copilot states locally.
// Note: Next.js requires literal string access for process.env.NEXT_PUBLIC_* variables
// to be inlined at build time. Dynamic lookups like process.env[varName] won't work.
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";
const MOCK_FEATURE_FLAG = IS_DEVELOPMENT
  ? parseEnvBoolean(process.env.NEXT_PUBLIC_MOCK_COPILOT_FEATURE_FLAG)
  : undefined;
const MOCK_DISABLED = IS_DEVELOPMENT
  ? parseEnvBoolean(process.env.NEXT_PUBLIC_MOCK_COPILOT_DISABLED)
  : undefined;

/**
 * Provider that fetches user data, creates an ai-toolkit compatible profile,
 * and computes the copilot enabled/disabled state.
 *
 * Use this to provide copilot state to components throughout the app.
 *
 * For local development, you can override states via env variables:
 * - NEXT_PUBLIC_MOCK_COPILOT_FEATURE_FLAG: "on"/"off" - override feature flag
 * - NEXT_PUBLIC_MOCK_COPILOT_DISABLED: "on"/"off" - override disabled state
 */
export function CopilotContextProvider({
  children,
}: CopilotContextProviderProps) {
  const {
    user,
    isLoading: isLoadingUser,
    error: userError,
  } = useCurrentUserContext();

  // Create profile for ai-toolkit hooks
  const profile = useMemo(() => createUserProfile(user), [user]);

  // Check ai-toolkit feature flag for copilot chat availability
  // This only works for logged-in users; logged-out users will see the search bar instead
  const { isEnabled: featureFlagValue, ready: isFeatureFlagsReady } =
    useIsCopilotEnabled("support", profile);

  // Copilot is enabled if the ai-toolkit flag is on for this user
  const isCopilotEnabled = MOCK_FEATURE_FLAG ?? featureFlagValue;

  // Check enterprise opt-out
  const accountId = user?.current_account_id;
  const {
    isCopilotOptOut,
    isLoading: isLoadingEntitlements,
    error: entitlementsError,
  } = useAccountEntitlements(accountId);

  // Check no-pilot experiment
  const { featureFlags } = useCopilotTreatments(profile);
  const isCopilotNoPilotDisabled =
    featureFlags.experimentNoPilot === "copilot_off";

  // Compute combined disabled state
  const isCopilotDisabled =
    MOCK_DISABLED ?? (isCopilotOptOut || isCopilotNoPilotDisabled);

  // Compute ready state
  // For logged-out users, don't wait for ai-toolkit feature flags - they won't use copilot anyway
  const isLoading = isLoadingUser || isLoadingEntitlements;
  const isReady =
    !isLoading && (isFeatureFlagsReady || user?.is_logged_in === false);

  // Combine errors
  const error = userError || entitlementsError;

  const value = useMemo(
    () => ({
      profile,
      isCopilotEnabled,
      isCopilotDisabled,
      isCopilotOptOut,
      isCopilotNoPilotDisabled,
      isReady,
      error,
    }),
    [
      profile,
      isCopilotEnabled,
      isCopilotDisabled,
      isCopilotOptOut,
      isCopilotNoPilotDisabled,
      isReady,
      error,
    ],
  );

  return (
    <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
  );
}

/**
 * Hook to access copilot state.
 * Must be used within a CopilotContextProvider.
 *
 * @example
 * // Check if copilot chat is available (show copilot input vs search bar)
 * const { isCopilotEnabled } = useCopilotContext();
 *
 * @example
 * // Check if copilot chat is disabled (enterprise opt-out or no-pilot experiment)
 * const { isCopilotDisabled } = useCopilotContext();
 *
 * @example
 * // Access profile for ai-toolkit hooks
 * const { profile } = useCopilotContext();
 */
export function useCopilotContext(): CopilotContextType {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error(
      "useCopilotContext must be used within a CopilotContextProvider",
    );
  }
  return context;
}

/**
 * Safe version of useCopilotContext that returns null when outside provider.
 * Use this in components that may render outside CopilotContextProvider.
 *
 * @example
 * const copilotContext = useCopilotContextSafe();
 * const isCopilotEnabled = copilotContext?.isCopilotEnabled ?? false;
 */
export function useCopilotContextSafe(): CopilotContextType | null {
  return useContext(CopilotContext);
}

export default CopilotContext;
